import { Color3 } from "@babylonjs/core/Maths/math.color";
import { isBoolean } from "../../utils/boolean";
import { eq } from "../../utils/math";
import { isNumber } from "../../utils/number";
import { isNullOrEmpty, isString } from "../../utils/string";
import { PropertyChangedResult } from "./PropertyProvider";
export const PropertyType = {
    String: 0,
    Bool: 1,
    Number: 2,
    Length: 3,
    Color3: 4,
    Angle: 5
};
export class PropertyValue {
    constructor(pValue = null, pText = null) {
        this.value = pValue;
        this.text = pText;
        this.smallIcon = null;
        this.largeIcon = null;
        this.image = null;
    }
    clone() {
        const tClone = new PropertyValue(this.value, this.text);
        tClone.smallIcon = this.smallIcon;
        tClone.largeIcon = this.largeIcon;
        tClone.image = this.image;
        tClone.surcharge = this.surcharge;
        return (tClone);
    }
}
export class PropertyClass {
    constructor(pKey = null, pName = null) {
        this.key = pKey != null ? pKey : "";
        this.name = pName;
    }
    /**
     * Returns the class name if it is not empty, otherwise the key is returned.
     */
    getName() {
        return (isNullOrEmpty(this.name) ? this.key : this.name);
    }
}
export class Property {
    get provider() {
        return (this.mProvider);
    }
    constructor(pProvider, pKey, pName = null) {
        this.mProvider = pProvider;
        this.key = pKey;
        this.name = pName;
        this.class = null;
        this.editable = true;
        this.visible = true;
        this.undoable = true;
        this.choiceList = false;
        this.allowCustomChoice = false;
        this.userData = null;
        //default callbacks
        this.getValue = () => null;
        this.setValue = (pValue) => Promise.resolve(PropertyChangedResult.Nothing);
        this.getChoices = () => Promise.resolve(null);
    }
    /**
     * Returns the property name if it is not empty, otherwise the key is returned.
     */
    getName() {
        return (isNullOrEmpty(this.name) ? this.key : this.name);
    }
    /**
     * Returns pOtherValue as a string. If pOther = undefined, the current value of this
     * property will be returned as a string.
     */
    getValueAsString(pOther) {
        const tValue = pOther === undefined ? this.getValue() : pOther;
        if (tValue == null || tValue.value == null)
            return (null);
        return (tValue.value.toString());
    }
    isValueEqual(pOther) {
        const tValue = this.getValue();
        if (tValue == null && pOther == null) //both are null -> equal
            return (true);
        if (tValue == null || pOther == null) //one is null -> not equal
            return (false);
        return (tValue.value === pOther.value);
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    _setProvider(pProvider) {
        if (pProvider != null)
            this.mProvider = pProvider;
    }
    copyFrom(pOther) {
        pOther.class = this.class;
        pOther.key = this.key;
        pOther.name = this.name;
        pOther.mProvider = this.provider;
        pOther.editable = this.editable;
        pOther.visible = this.visible;
        pOther.undoable = this.undoable;
        pOther.choiceList = this.choiceList;
        pOther.allowCustomChoice = this.allowCustomChoice;
        pOther.userData = this.userData;
        pOther.getValue = this.getValue;
        pOther.setValue = this.setValue;
        pOther.getChoices = this.getChoices;
    }
}
/**
 * StringProperty (PropertyValueType = string)
 */
export class StringProperty extends Property {
    constructor(pProvider, pKey, pName) {
        super(pProvider, pKey, pName);
        this.type = PropertyType.String;
        this.maxLength = null;
        this.multiLine = false;
    }
    clone() {
        const tClone = new StringProperty(this.provider, this.key, this.name);
        this.copyFrom(tClone);
        return (tClone);
    }
    static ConvertValueToString(pValue) {
        if (pValue == null)
            return (null);
        return (pValue.toString());
    }
    //// protected /////////////////////////////////////////////////////////////////////////////
    copyFrom(pOther) {
        super.copyFrom(pOther);
        if (pOther instanceof StringProperty) {
            pOther.maxLength = this.maxLength;
            pOther.multiLine = this.multiLine;
        }
    }
}
/**
 * BoolProperty (PropertyValueType = boolean)
 */
export class BoolProperty extends Property {
    constructor() {
        super(...arguments);
        this.type = PropertyType.Bool;
    }
    clone() {
        const tClone = new BoolProperty(this.provider, this.key, this.name);
        this.copyFrom(tClone);
        return (tClone);
    }
    static ConvertValueToBool(pValue) {
        if (pValue == null)
            return (null);
        if (isBoolean(pValue))
            return (pValue);
        if (isString(pValue))
            return (pValue === "true");
        if (isNumber(pValue))
            return (pValue != 0);
        return (false);
    }
}
/**
 * NumberProperty (PropertyValueType = number)
 */
export class NumberProperty extends Property {
    constructor(pProvider, pKey, pName) {
        super(pProvider, pKey, pName);
        this.type = PropertyType.Number;
        this.minValue = null;
        this.maxValue = null;
        this.numDigits = null;
        this.numDecDigits = 0;
    }
    isValueEqual(pOther) {
        if (super.isValueEqual(pOther))
            return (true);
        //check if epsilon equal
        const tValue = this.getValue();
        if (tValue != null && tValue.value != null &&
            pOther != null && pOther.value != null) {
            return (eq(tValue.value, NumberProperty.ConvertValueToNumber(pOther.value)));
        }
        return (false);
    }
    clone() {
        const tClone = new NumberProperty(this.provider, this.key, this.name);
        this.copyFrom(tClone);
        return (tClone);
    }
    static ConvertValueToNumber(pValue) {
        if (pValue == null)
            return (null);
        if (isNumber(pValue))
            return (pValue);
        if (isString(pValue))
            return (parseFloat(pValue));
        if (isBoolean(pValue))
            return (pValue ? 1 : 0);
        return (0);
    }
    //// protected /////////////////////////////////////////////////////////////////////////////
    copyFrom(pOther) {
        super.copyFrom(pOther);
        if (pOther instanceof NumberProperty) {
            pOther.minValue = this.minValue;
            pOther.maxValue = this.maxValue;
            pOther.numDigits = this.numDigits;
            pOther.numDecDigits = this.numDecDigits;
        }
    }
}
/**
 * LengthProperty (PropertyValueType = number)
 * The value represents a length in meter.
 */
export class LengthProperty extends NumberProperty {
    constructor(pProvider, pKey, pName) {
        super(pProvider, pKey, pName);
        this.type = PropertyType.Length;
        this.numDecDigits = 3;
    }
    clone() {
        const tClone = new LengthProperty(this.provider, this.key, this.name);
        this.copyFrom(tClone);
        return (tClone);
    }
}
/**
 * AngleProperty (PropertyValueType = number)
 * The value represents an angle in degree.
 */
export class AngleProperty extends NumberProperty {
    constructor(pProvider, pKey, pName) {
        super(pProvider, pKey, pName);
        this.type = PropertyType.Angle;
        this.numDecDigits = 2;
    }
    clone() {
        const tClone = new AngleProperty(this.provider, this.key, this.name);
        this.copyFrom(tClone);
        return (tClone);
    }
}
/**
 * Color3Property (PropertyValueType = string)
 * The color is represented as an RGB hex string, e.g. #FF0000
 */
export class Color3Property extends Property {
    constructor() {
        super(...arguments);
        this.type = PropertyType.Color3;
    }
    clone() {
        const tClone = new Color3Property(this.provider, this.key, this.name);
        this.copyFrom(tClone);
        return (tClone);
    }
    /**
     * Converts pValue into a hex color string (if it is not already a string).
     */
    static ConvertValueToString(pValue) {
        if (pValue == null)
            return (null);
        if (isBoolean(pValue))
            return (pValue ? "#FFFFFF" : "#000000");
        if (isString(pValue))
            return (pValue);
        if (isNumber(pValue))
            return (pValue !== 0 ? "#FFFFFF" : "#000000");
        return ("#000000");
    }
    /**
     * Converts pValue into a Color3
     */
    static ConvertValueToColor3(pValue) {
        const tStrValue = Color3Property.ConvertValueToString(pValue);
        if (tStrValue == null)
            return (null);
        return (Color3.FromHexString(tStrValue));
    }
}
